import chroma from "chroma-js"

const MIN_CONTRAST_RATIO_BLACK = 3.5 // WCAG AA standard for normal text
const MIN_CONTRAST_RATIO_WHITE = 2.5 // Lower threshold for white text since it's generally more readable
const MIN_SATURATION = 0.3 // Minimum saturation threshold (30%)
const BLACK = "#000000"
const WHITE = "#FFFFFF"
const SATURATE_AMOUNT = 0.5 // Amount to darken colors that don't meet white contrast requirements

/**
 * Selects the best color for the chart from a list of colors based on contrast and saturation
 * @param colors - List of colors to select from
 * @returns The best color for the chart or undefined if no color is suitable
 */
export function selectBestChartColor(colors: string[]): string | undefined {
  if (!colors.length) {
    return undefined
  }

  // Calculate metrics for each color
  const colorsWithMetrics = colors
    .map(color => {
      const chromaColor = chroma(color)
      const contrastWithWhite = chroma.contrast(color, WHITE)

      // If contrast with white is too low, darken and saturate the color
      let adjustedColor = color
      if (contrastWithWhite < MIN_CONTRAST_RATIO_WHITE) {
        adjustedColor = chromaColor.darken(1).saturate(SATURATE_AMOUNT).hex()
      }

      const adjustedContrastWithWhite = chroma.contrast(adjustedColor, WHITE)
      const contrastWithBlack = chroma.contrast(adjustedColor, BLACK)
      const saturation = chroma(adjustedColor).get("hsl.s")

      return {
        color: adjustedColor,
        originalColor: color,
        contrastWithBlack,
        contrastWithWhite: adjustedContrastWithWhite,
        saturation,
      }
    })
    .filter(
      colorMetrics =>
        colorMetrics.contrastWithBlack >= MIN_CONTRAST_RATIO_BLACK &&
        colorMetrics.contrastWithWhite >= MIN_CONTRAST_RATIO_WHITE &&
        colorMetrics.saturation >= MIN_SATURATION,
    )

  if (colorsWithMetrics.length === 0) {
    return undefined
  }

  // Select the most saturated color from those that meet the contrast requirement
  const bestColor = colorsWithMetrics[0]

  return bestColor.color
}
